import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Audience = 'older_adult' | 'family' | null;

interface AudienceContextType {
  audience: Audience;
  setAudience: (audience: Audience) => void;
}

const AudienceContext = createContext<AudienceContextType | undefined>(undefined);

export const AudienceProvider = ({ children }: { children: ReactNode }) => {
  const [audience, setAudienceState] = useState<Audience>(() => {
    // Check URL param first
    const urlParams = new URLSearchParams(window.location.search);
    const urlAudience = urlParams.get('audience');
    if (urlAudience === 'older_adult' || urlAudience === 'family') {
      return urlAudience;
    }
    // Then check localStorage
    const stored = localStorage.getItem('seniorthrive_audience');
    if (stored === 'older_adult' || stored === 'family') {
      return stored;
    }
    return null;
  });

  const setAudience = (newAudience: Audience) => {
    setAudienceState(newAudience);
    if (newAudience) {
      localStorage.setItem('seniorthrive_audience', newAudience);
      // Update URL without page reload
      const url = new URL(window.location.href);
      url.searchParams.set('audience', newAudience);
      window.history.replaceState({}, '', url.toString());
    } else {
      localStorage.removeItem('seniorthrive_audience');
      const url = new URL(window.location.href);
      url.searchParams.delete('audience');
      window.history.replaceState({}, '', url.toString());
    }
  };

  return (
    <AudienceContext.Provider value={{ audience, setAudience }}>
      {children}
    </AudienceContext.Provider>
  );
};

export const useAudience = () => {
  const context = useContext(AudienceContext);
  if (context === undefined) {
    throw new Error('useAudience must be used within an AudienceProvider');
  }
  return context;
};
